import { useEffect } from "react";
import { AddHeader } from "../../components/AddHeader";
import { useTranslation } from "react-i18next";
import { useBrand } from "../../context/BrandContext";
import {
  PRIVACY_POLICY_VERSION,
  PRIVACY_CONTACT,
  PING4ALL_ADDRESS,
  DPO_NAME,
  DPO_EMAIL,
  GCP_REGION,
  HEALTH_RECORD_HOST,
  EDGE_DEVICE_STORES_PATIENT_RECORDS,
} from "../../constants/legalPolicy";
import { LegalFooter } from "../../components/legal/LegalFooter";
import "./PrivacyNotice.css";

export default function PrivacyNoticePage() {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const club = brand?.brand_name || t("yourClub", "Your club");

  useEffect(() => {
    document.title = t("privacyPolicy", "Privacy notice") + " · SmartTT";
  }, [t]);

  return (
    <div className="privacy-page">
      <AddHeader title={t("privacyPolicy", "Privacy notice")} />
      <article className="privacy-page__body">
        <p className="privacy-page__meta">
          {t("privacyVersion", "Version")} {PRIVACY_POLICY_VERSION}
        </p>
        <p>
          {club} {t(
            "privacyIntro",
            "uses SmartTT, built by Ping4All, to plan training and track progress. Your club is the data controller. Ping4All is the processor and runs the software on the club's instructions. Your account and training records are processed because they are necessary for that service (a contract). Video recording and heart-rate measurement are optional and need separate consent."
          )}
        </p>

        <h2>{t("privacyWho", "Who is responsible")}</h2>
        <ul>
          <li>
            <strong>{t("privacyController", "Controller")}:</strong> {club}
          </li>
          <li>
            <strong>{t("privacyProcessor", "Processor")}:</strong> Ping4All,{" "}
            {PING4ALL_ADDRESS} — {PRIVACY_CONTACT}
          </li>
          <li>
            <strong>{t("privacyDpo", "Data Protection Officer")}:</strong>{" "}
            {DPO_NAME} —{" "}
            <a href={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</a>
          </li>
        </ul>

        <h2>{t("privacyWhat", "What is collected")}</h2>
        <p>
          {t(
            "privacyWhatBody",
            "Account data (name, date of birth, email, club role), training records, and technical logs — necessary to provide SmartTT. Training video, movement analysis, and heart rate are collected only with a separate grant. Heart rate is health data."
          )}
        </p>

        <h2>{t("privacyRights", "Your rights")}</h2>
        <p>
          {t(
            "privacyRightsBody",
            "You can access, correct, delete, restrict, port, or object. You can withdraw consent for video and heart rate at any time. Ending your membership ends account processing except where the law requires a record. Ask your club first. If they cannot help, email"
          )}{" "}
          <a href={`mailto:${PRIVACY_CONTACT}`}>{PRIVACY_CONTACT}</a>.{" "}
          {t("privacyRightsDeadline", "We respond within one month.")}{" "}
          {t(
            "privacyAuthority",
            "You can also complain to the CNPD in Luxembourg (cnpd.lu) or your national authority."
          )}
        </p>

        <h2>{t("privacySharing", "Who it is shared with")}</h2>
        <p>
          {t("privacySharingBody", {
            defaultValue:
              "Coaches and administrators in your club. Ping4All staff only as needed to run the platform. Hosting, storage and video processing run on Google Cloud in the EU ({{region}}). We do not sell your data and we do not use it for advertising. Statistics that cannot identify you may be used to improve SmartTT.",
            region: GCP_REGION,
          })}
        </p>
        <p>
          {t("privacyHealthHosting", {
            defaultValue:
              "A prescription file (end date and a functional goal, not a diagnosis) is stored apart from the training group on {{host}} in the EU ({{region}}), limited to services covered by Google Cloud's HDS certification. A camera computer in the gym may analyse movement and send metrics. It does not keep the patient file.",
            host: HEALTH_RECORD_HOST,
            region: GCP_REGION,
            edgeStores: EDGE_DEVICE_STORES_PATIENT_RECORDS,
          })}
        </p>
        <p>
          {t(
            "privacyEuBody",
            "Personal data and AI prompts stay in the European Union. Prompts to the coaching assistant do not include your name, email or photo; a coded player reference is used instead so you are not identified in the model request."
          )}
        </p>
        <p>
          {t(
            "privacyStoresBody",
            "If you install the iOS or Android app, Apple or Google process device and store-account data as independent controllers under their own policies. Ping4All does not control that processing."
          )}
        </p>
        <p>
          {t(
            "privacyDeviceBody",
            "The mobile app may ask for camera, photos or notifications so those features work. You can refuse in the device settings; some features then will not run."
          )}
        </p>

        <h2>{t("privacyCookies", "Cookies")}</h2>
        <p>
          {t(
            "privacyCookiesBody",
            "SmartTT uses browser storage only to keep you signed in, remember language, and show club branding. That is strictly necessary. There is no advertising or analytics tracker, so no cookie banner is shown."
          )}
        </p>

        <h2>{t("privacyChildren", "Children")}</h2>
        <p>
          {t(
            "privacyChildrenBody",
            "Players under 18: one parent or guardian accepts the terms so the child can have an account. Coach video, public image and heart rate are separate optional consents the parent gives in the app. A paper form is only a fallback. A child can train fully without being recorded."
          )}
        </p>

        <p className="privacy-page__full">
          {t(
            "privacyFullPack",
            "The full Art. 13 notice, retention schedule and DPIA live with your club administrator."
          )}
        </p>
        <LegalFooter />
      </article>
    </div>
  );
}
